import { useState } from 'react';
import { toast } from 'sonner';
import { MessageSquarePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useBoardActivity, usePostBoardUpdate } from '@/hooks/use-board-overview';

interface ProjectUpdatesProps {
  boardId: string;
}

/**
 * Composer-only affordance for posting a project update. The posted updates
 * themselves surface in the Activity feeds (Activity tab + Overview sidebar),
 * not here.
 */
export function ProjectUpdates({ boardId }: ProjectUpdatesProps) {
  const { data: activity } = useBoardActivity(boardId);
  const post = usePostBoardUpdate(boardId);
  const [composing, setComposing] = useState(false);
  const [body, setBody] = useState('');

  // Wait for the feed before choosing the CTA, so a board with updates doesn't
  // flash the "write first update" dashed button before the compact one.
  const hasLoaded = activity !== undefined;
  const hasUpdates = (activity ?? []).some((item) => item.kind === 'update');

  const cancel = () => {
    setComposing(false);
    setBody('');
  };

  const submit = () => {
    const text = body.trim();
    if (!text) return;
    post.mutate(text, {
      onSuccess: () => {
        setBody('');
        setComposing(false);
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to post update');
      },
    });
  };

  if (composing) {
    return (
      <section className="rounded-lg border border-border bg-card p-3">
        <Textarea
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Share an update with the team…"
          className="min-h-[96px] resize-y text-sm"
        />
        <div className="mt-2.5 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" className="cursor-pointer" onClick={cancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="cursor-pointer"
            disabled={!body.trim() || post.isPending}
            onClick={submit}
          >
            Post update
          </Button>
        </div>
      </section>
    );
  }

  if (!hasLoaded) {
    return <div className="h-9" aria-hidden />;
  }

  if (hasUpdates) {
    return (
      <button
        type="button"
        onClick={() => setComposing(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:border-hygge/40 hover:text-foreground cursor-pointer"
      >
        <MessageSquarePlus className="h-4 w-4" />
        Post update
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setComposing(true)}
      className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 py-4 text-sm font-medium text-muted-foreground transition-colors hover:border-hygge/40 hover:text-foreground cursor-pointer"
    >
      <MessageSquarePlus className="h-4 w-4" />
      Write first project update
    </button>
  );
}
